// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';
import React, { PropsWithChildren } from 'react';
import { Outlet, RouteObject, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import UtilitiesSnackbar from './components/UtilitiesSnackbar';
import { JSON_API_URL, VALIDATOR_API_URL } from './config/externalApis';
import { AllocationApiProvider } from './contexts/AllocationApiProvider';
import { useBranding } from './contexts/BrandingContext';
import { IdpProvider } from './contexts/IdpProvider';
import { JsonApiProvider } from './contexts/JsonApiProvider';
import { LedgerApiClientProvider } from './contexts/LedgerApiProvider';
import { useLocalStorage } from './contexts/LocalStorageContext';
import { LocalStorageProvider } from './contexts/LocalStorageProvider';
import { ModuleProvider } from './contexts/ModuleProvider';
import { PartiesProvider } from './contexts/PartiesProvider';
import { ScanProxyApiProvider } from './contexts/ScanProxyApiProvider';
import { UserProvider } from './contexts/UserProvider';
import { UtilityAutomationApiClientProvider } from './contexts/UtilityAutomationApiClientProvider';
import { UtilityOperatorProxyApiProvider } from './contexts/UtilityOperatorApiProvider';
import { useConfig } from './hooks/other/useConfig';
import { Header } from './layout/Header';
import collateral from './modules/Collateral';
import { Credential } from './modules/Credential';
import { Registry } from './modules/Registry';
import { AppSettingsLayout } from './pages/AppSettingsLayout';
import AuthProvider from './pages/AuthProvider';
import Login from './pages/Login';
import { NotFound } from './pages/NotFound';
import { AllSettings } from './pages/settings/AllSettings';
import { AppRewardConfigurationsPage } from './pages/settings/AppRewardConfigurationsPage';
import { CommercialAgreements } from './pages/settings/CommercialAgreements';
import { Versions } from './pages/settings/versions/Versions';
import { AuthCheck } from './routes/AuthCheck';
import Root from './routes/Root';
import { queryClient } from './utils/QueryClient';

const AuthProviders: React.FC<PropsWithChildren> = () => {
  return (
    <IdpProvider>
      <AuthProvider>
        <UserProvider>
          <Outlet />
        </UserProvider>
      </AuthProvider>
    </IdpProvider>
  );
};

const CommonProviders: React.FC = () => {
  const config = useConfig();
  const { localStorageState } = useLocalStorage();
  const { pollingEnabled } = localStorageState;
  const utilityBackendUrl = config.app.utility_backend_url;

  return (
    <QueryClientProvider client={queryClient(pollingEnabled)}>
      <ReactQueryDevtools initialIsOpen={false} />
      <SnackbarProvider
        Components={{
          utilities: UtilitiesSnackbar,
        }}
      >
        <UtilityOperatorProxyApiProvider utilityOperatorUrl={utilityBackendUrl}>
          <AllocationApiProvider url={utilityBackendUrl}>
            <JsonApiProvider jsonApiUrl={JSON_API_URL}>
              <ScanProxyApiProvider validatorUrl={VALIDATOR_API_URL}>
                <UtilityAutomationApiClientProvider>
                  <Outlet />
                </UtilityAutomationApiClientProvider>
              </ScanProxyApiProvider>
            </JsonApiProvider>
          </AllocationApiProvider>
        </UtilityOperatorProxyApiProvider>
      </SnackbarProvider>
    </QueryClientProvider>
  );
};

const utilityRoutes: RouteObject[] = [
  collateral,
  { path: 'credential/*', element: <Credential /> }, // has topbar, has sidebar
  { path: 'registry/*', element: <Registry /> }, // has topbar, has sidebar
  {
    path: 'settings',
    element: <AppSettingsLayout />,
    children: [
      { path: '', element: <AllSettings /> },
      { path: 'commercial-agreements', element: <CommercialAgreements /> },
      { path: 'versions', element: <Versions /> },
      { path: 'app-reward-configs', element: <AppRewardConfigurationsPage /> },
    ],
  },
];

export const App: React.FC = () => {
  const { theme } = useBranding();

  const appRoutes = utilityRoutes;
  const sidebarLayout = <Outlet />;
  const headerLayout = (
    <PartiesProvider>
      <ModuleProvider>
        <LedgerApiClientProvider>
          <Header />
        </LedgerApiClientProvider>
      </ModuleProvider>
    </PartiesProvider>
  );

  const loginPage = <Login />;
  const indexPage = <Root />;

  const routes: RouteObject = {
    element: <AuthProviders />,
    errorElement: <NotFound />,
    children: [
      { path: 'login', element: loginPage },
      {
        element: <AuthCheck />,
        children: [
          {
            element: <CommonProviders />,
            children: [
              {
                element: headerLayout,
                children: [
                  { path: '/', element: indexPage, index: true },
                  { element: sidebarLayout, children: appRoutes },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  return (
    <LocalStorageProvider>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <RouterProvider
            future={{ v7_startTransition: true }}
            router={createBrowserRouter([routes], {
              future: {
                // Disable this for now because the self-hosted modules use splat paths, which break under the new v7 behavior.
                v7_relativeSplatPath: false,
              },
            })}
          />
        </ThemeProvider>
      </StyledEngineProvider>
    </LocalStorageProvider>
  );
};
